import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { useNotify } from '@/hooks/useNotify';
import {
  useAutomationRecipes,
  useCreateAutomationRule,
  type AutomationRecipe,
} from '@/hooks/useAutomation';

function catalogKey(key: string): string {
  return key.replace(/^automation\./, '');
}

interface CreateRuleDialogProps {
  open: boolean;
  onClose: () => void;
  preselectedRecipeId?: string | null;
  insightPrefill?: { insightId: string; insightType: string; targetEntities?: unknown } | null;
}

export function CreateRuleDialog({
  open,
  onClose,
  preselectedRecipeId,
  insightPrefill,
}: CreateRuleDialogProps) {
  const { t } = useTranslation('automation');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const recipesQuery = useAutomationRecipes(open);
  const createRule = useCreateAutomationRule();

  const recipes = useMemo(
    () => recipesQuery.data ?? [],
    [recipesQuery.data],
  );
  const [recipeId, setRecipeId] = useState(preselectedRecipeId ?? '');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [taskTitle, setTaskTitle] = useState('');
  const [priority, setPriority] = useState('normal');

  const recipe: AutomationRecipe | undefined = useMemo(
    () => recipes.find((item) => item.id === recipeId),
    [recipes, recipeId],
  );

  useEffect(() => {
    if (!open) return;
    const nextId = preselectedRecipeId ?? recipes[0]?.id ?? '';
    setRecipeId(nextId);
  }, [open, preselectedRecipeId, recipes]);

  useEffect(() => {
    if (!recipe || !open) return;
    setName(t(catalogKey(recipe.titleKey)));
    setDescription(t(catalogKey(recipe.descriptionKey)));
    const defaults = recipe.defaultParameters;
    setTaskTitle(typeof defaults['taskTitle'] === 'string' ? defaults['taskTitle'] : '');
    setPriority(typeof defaults['priority'] === 'string' ? defaults['priority'] : 'normal');
  }, [recipe, open, t]);

  const handleSave = async () => {
    if (!recipe) return;
    try {
      await createRule.mutateAsync({
        recipeId: recipe.id,
        name: name.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
        parameters: {
          ...(taskTitle.trim() ? { taskTitle: taskTitle.trim() } : {}),
          priority,
        },
      });
      notify.success(t('created'));
      onClose();
    } catch {
      notify.error(t('createFailed'));
    }
  };

  return (
    <ResponsiveDialog open={open} onClose={onClose} maxWidth="sm" fullWidth mobileTitle={t('createTitle')}>
      <DialogTitle>{t('createTitle')}</DialogTitle>
      <DialogContent>
      <Stack spacing={2} sx={{ pt: 1 }}>
        {insightPrefill && <Alert severity="info">{t('automateThisHint')}</Alert>}
        <FormControl fullWidth>
          <InputLabel>{t('recipe')}</InputLabel>
          <Select
            label={t('recipe')}
            value={recipeId}
            onChange={(e) => setRecipeId(e.target.value)}
          >
            {recipes.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {t(catalogKey(item.titleKey))}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {recipe && (
          <Alert severity="info">
            {t(catalogKey(recipe.descriptionKey))} · {t(`risk.${recipe.riskClass}`, recipe.riskClass)}
          </Alert>
        )}
        <TextField label={t('name')} value={name} onChange={(e) => setName(e.target.value)} required />
        <TextField
          label={t('description')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          minRows={2}
        />
        {recipe?.parameterKeys.includes('taskTitle') && (
          <TextField
            label={t('taskTitle')}
            value={taskTitle}
            onChange={(e) => setTaskTitle(e.target.value)}
          />
        )}
        {recipe?.parameterKeys.includes('priority') && (
          <FormControl fullWidth>
            <InputLabel>{t('priority')}</InputLabel>
            <Select label={t('priority')} value={priority} onChange={(e) => setPriority(e.target.value)}>
              <MenuItem value="low">{t('priorityLow')}</MenuItem>
              <MenuItem value="normal">{t('priorityNormal')}</MenuItem>
              <MenuItem value="high">{t('priorityHigh')}</MenuItem>
            </Select>
          </FormControl>
        )}
      </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{tc('cancel')}</Button>
        <Button
          variant="contained"
          onClick={() => void handleSave()}
          disabled={!recipe || !name.trim() || createRule.isPending}
        >
          {tc('save')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
